import { list } from '../../../../../../common/classes/request-builder';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import { InputType, DropdownData } from '../../../../../../common/components/reactive-form-builder/interfaces/input.interface';
import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '../../../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { environment } from '../../../../../../../environments/environment';
import { ClienteModel } from '../../../../../../models/cliente.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { ControlSanitarioModel } from '../../../../../../models/tareas/control-sanitario.model';

export class ControlSanitarioFormDefinition {
    public readonly hasClients = environment.features.hasClients;
    public readonly fincaName = environment.features.fincaName;

    public formType!: FormType;
    public model: Partial<ControlSanitarioModel> = {};
    public formFields!: Form<ControlSanitarioModel>;
    public fieldNames: FormField<ControlSanitarioModel>[] = [];
    public fieldLabels: string[] = [];

    public fincas: DropdownData<FincasModel> = list();
    public clientes: DropdownData<ClienteModel> = list();
    public tiposTratamientos: DropdownData<string> = list(
        ['...', null],
        'ENFERMEDADES INFECCIOSAS',
        'INMUNIZACIÓN',
        'VACUNACIÓN',
        'DESPARASITACIÓN',
    );

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<ControlSanitarioModel> = {}) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'control-sanitario',
            header: 'Datos del control sanitario',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    private getFormSections(): FormSection<ControlSanitarioModel>[] {
        const sections: FormSection<ControlSanitarioModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getTreatmentFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getMainFields(): FormField<ControlSanitarioModel>[] {
        const fincaFieldName = this.formType < FormType.DELETION ? 'id_finca' : 'finca';
        const fincaInputType = this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.TEXT;

        const fields: FormField<ControlSanitarioModel>[] = [
            {
                name: fincaFieldName,
                vinculatedFields: ['id_cliente'],
                label: this.fincaName,
                input: {
                    type: fincaInputType,
                    data: this.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterFincaByClient(it.value as FincasModel | null),
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'fecha_inicio',
                vinculatedFields: undefined,
                label: 'Fecha inicio',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'fecha_fin',
                vinculatedFields: undefined,
                label: 'Fecha fin',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'fecha_plazo',
                vinculatedFields: undefined,
                label: 'Plazo de seguridad',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getTreatmentFields(): FormField<ControlSanitarioModel>[] {
        const fields: FormField<ControlSanitarioModel>[] = [
            {
                name: 'tipo_tratamiento',
                vinculatedFields: undefined,
                label: 'Tipo de tratamiento',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.tiposTratamientos,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'veterinario',
                vinculatedFields: undefined,
                label: 'Veterinario',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'diagnostico',
                vinculatedFields: undefined,
                label: 'Diagnóstico',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'producto',
                vinculatedFields: undefined,
                label: 'Producto',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'animales_tratados',
                vinculatedFields: undefined,
                label: 'Animales tratados',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'observaciones',
                vinculatedFields: undefined,
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 6,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getHiddenFields(): FormField<ControlSanitarioModel>[] {
        return [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id ?? null,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveInitialClientId(),
                    isEditable: false,
                },
                updateValue: () => this.resolveSelectedClientId(),
                width: 2,
                conditions: this.conditions({ isVisible: this.hasClients }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveUserId(),
                    isEditable: false,
                },
                updateValue: () => this.resolveUserId(),
                width: 2,
                conditions: this.conditions(),
                order: 3,
            },
        ];
    }

    private registerVisibleFields(fields: FormField<ControlSanitarioModel>[]): FormField<ControlSanitarioModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);
        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private filterFincaByClient(finca: FincasModel | null): boolean {
        if (finca === null || !this.hasClients || this.formType === FormType.DUPLICATION) {
            return true;
        }

        const selectedClientId = this.resolveClientId(this.model.id_cliente) || this.client?.id || this.clientes.selected?.id;

        if (!selectedClientId) {
            return true;
        }

        return finca.id_cliente?.toString() === selectedClientId.toString();
    }

    private resolveInitialClientId(): string | null {
        return this.resolveClientId(this.model.id_cliente) || this.client?.id || null;
    }

    private resolveSelectedClientId(): string {
        const selectedFinca = this.fincas.selected;

        if (selectedFinca?.id_cliente !== undefined && selectedFinca.id_cliente !== null) {
            return String(selectedFinca.id_cliente);
        }

        return this.resolveInitialClientId() ?? '';
    }

    private resolveClientId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }

    private resolveUserId(): string {
        return String(this.model.id_usuario ?? this.user?.id ?? '');
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
